import { ICoreContext } from '@msdyn365-commerce/core';
import { computed, toJS } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import getDirectLineTokenAction, { GetDirectLineTokenInput } from '../../actions/get-direct-line-token.action';
import { PrivilegedWrapper } from '../../components/access-control';
import { IUserRoleState } from '../../states/user-role-state';
import { isJwtExpired } from '../../utilities/token-utils';
import { ILxsServiceAgentData, IStoredConversation, STORAGE_KEY } from './lxs-service-agent.data';
import { ILxsServiceAgentProps } from './lxs-service-agent.props.autogenerated';

/**
 *
 * LxsServiceAgent component
 * @extends {React.PureComponent<ILxsServiceAgentProps<ILxsServiceAgentData>>}
 */

@observer
class LxsServiceAgent extends React.PureComponent<ILxsServiceAgentProps<ILxsServiceAgentData>> {
    private __getDirectLineToken = async (context: ICoreContext): Promise<string | null> => {
        try {
            if (!context.actionContext) {
                return null;
            }
            const input = new GetDirectLineTokenInput(context.actionContext);
            const directLineTokenResponse = await getDirectLineTokenAction(input, context.actionContext);
            if (!directLineTokenResponse) {
                return null;
            } else {
                return String(directLineTokenResponse.value);
            }
        } catch (err) {
            return null;
        }
    };

    private __readStoredConversation = (): IStoredConversation | null => {
        try {
            const storedConversationRaw = sessionStorage.getItem(STORAGE_KEY);
            if (!storedConversationRaw) {
                return null;
            }
            return JSON.parse(storedConversationRaw) as IStoredConversation;
        } catch {
            sessionStorage.removeItem(STORAGE_KEY);
            return null;
        }
    };

    private __writeStoredConversation = (conversation: IStoredConversation): void => {
        try {
            sessionStorage.setItem(STORAGE_KEY, JSON.stringify(conversation));
        } catch (e) {
            console.error('Failed to write webchat blob', e);
        }
    };

    private __clearStoredConversation = (): void => {
        try {
            sessionStorage.removeItem(STORAGE_KEY);
        } catch {
            return;
        }
    };

    private __getValidStoredConversation = (directLineToken: string, userToken: string): IStoredConversation | null => {
        const tokenExpired = isJwtExpired(String(directLineToken));
        const stored = this.__readStoredConversation();

        if (
            !stored ||
            tokenExpired ||
            stored.directLineToken !== String(directLineToken) ||
            stored.userToken !== userToken ||
            !stored.conversationId ||
            !Array.isArray(stored.transcript)
        ) {
            this.__clearStoredConversation();
            return null;
        }

        return stored;
    };

    @computed
    get userRole(): IUserRoleState {
        const raw = toJS(this.props.data?.userRoleState?.result);
        if (!raw) {
            return {} as IUserRoleState;
        }
        return raw;
    }

    public render(): JSX.Element | null {
        const userRole = this.userRole;
        const directLineToken = this.__getDirectLineToken;
        const viewProps = {
            ...this.props,
            directLineToken: directLineToken,
            CLASS_NAME: 'lxs-service-agent',
            fetchDirectLineToken: this.__getDirectLineToken,
            readStoredConversation: this.__readStoredConversation,
            writeStoredConversation: this.__writeStoredConversation,
            clearStoredConversation: this.__clearStoredConversation,
            getValidStoredConversation: this.__getValidStoredConversation
        };

        return (
            <PrivilegedWrapper allow={['service_agent']} roles={userRole.userRoles}>
                {this.props.renderView(viewProps)}
            </PrivilegedWrapper>
        );
    }
}

export default LxsServiceAgent;
